import { createApi } from "@reduxjs/toolkit/query/react";
import { baseProxyQueryWithReauth } from "./API";

export interface ReportingTopicType {
  uuid: string;
  label: string;
  description: string;
  isDefault: boolean;
  ticketStatuses: string[];
  isNew?: boolean;
}

interface ReportingState {
  reportingTopics: ReportingTopicType[];
}

const initialState: ReportingState = {
  reportingTopics: [],
};

export const reportingProxyAPI = createApi({
  reducerPath: "reportingProxyAPI",
  baseQuery: baseProxyQueryWithReauth("logic/reporting"),
  refetchOnMountOrArgChange: true,
  tagTypes: ["ReportingTemplates", "ReportingTopics", "ReportingEntries"],

  endpoints: (builder) => ({
    getReportingTemplates: builder.query<
      {
        data: any[];
      },
      { pagination: { page: number; perPage: number } }
    >({
      query: (params) => ({
        url: `/templates`,
        method: "GET",
        params: {
          "pagination.page": params.pagination.page,
          "pagination.perPage": params.pagination.perPage,
        },
      }),
      providesTags: ["ReportingTemplates"],
    }),
    createReportingTemplate: builder.mutation<any, any>({
      query: (body) => ({
        url: `/templates`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ReportingTemplates"],
    }),
    getReportingTopics: builder.query<
      {
        data: any[];
      },
      { pagination: { page: number; perPage: number } }
    >({
      query: (params) => ({
        url: `/topics`,
        method: "GET",
        params: {
          "pagination.page": params.pagination.page,
          "pagination.perPage": params.pagination.perPage,
        },
      }),
      providesTags: ["ReportingTopics"],
    }),
    createReportingTopic: builder.mutation<any, any>({
      query: (body) => ({
        url: `/topics`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ReportingTopics"],
    }),
    getReportingEntries: builder.query<
      {
        data: any[];
      },
      {
        pagination: {
          page: number;
          perPage: number;
        };
        filters: {
          topics?: string[];
          tickets?: string[];
          contactUuids?: string[];
          templateUuids?: string[];
          search?: string;
          dateGte?: string;
          dateLte?: string;
          // uuids: entries,
          values?: {
            fields: string[];
            operator: string;
            or: boolean;
            values: string[];
          }[];
        };
      }
    >({
      query: (body) => ({
        url: `/get-entries`,
        method: "POST",
        body,
      }),
      providesTags: ["ReportingEntries"],
    }),
    getMatchingReportingTemplates: builder.query<
      any,
      {
        label: string;
        fields: {
          label: string;
          default_value: string;
          type: number;
        }[];
      }
    >({
      query: (body) => ({
        url: `/templates/matching`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetReportingTemplatesQuery,
  useGetReportingTopicsQuery,
  useCreateReportingTemplateMutation,
  useCreateReportingTopicMutation,
  useGetReportingEntriesQuery,
  useGetMatchingReportingTemplatesQuery,
  useLazyGetMatchingReportingTemplatesQuery
} = reportingProxyAPI;
